import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { Button, Grid, IconButton, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { AkcClubNumber } from '../../../models/AkcClubNumber';
import { ConfigRecord } from '../../../models/ConfigRecord';
import { LabelAndUrl } from '../../../models/LabelAndUrl';
import { SecretaryEventConfig } from '../../../models/SecretaryEventConfig';
import './configViewSecretaryEvents.css';
import _ from 'lodash';
import moment from 'moment-timezone';

export default function ConfigViewSecretaryEvents() {
  const { email, jwtToken } = useAppSelector((state: any) => state.registrant);
  const [ configRecord, setConfigRecord ] = useState<ConfigRecord>();
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isDirty, setIsDirty ] = useState(false);
  const [ statusMessage, setStatusMessage ] = useState<string>('');
  const [ validationMessages, setValidationMessages ] = useState<string[][]>([]);
  const [ clubNumbers, setClubNumbers ] = useState<AkcClubNumber[]>([]);
  
   useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        /*
        const userRsp = await axios.get(`/api/protected/user/${email}`, {
          headers: { 
            Authorization: `Bearer ${jwtToken}`,
          }
        });
        */
        const clubNumbersRsp: any = await axios.get(`/api/constants/akc-club-numbers`);
        setClubNumbers(_.get(clubNumbersRsp, 'data.clubNumbers', []));

        const eventRsp: any = await axios.get(`api/config/events`, { params: { filtered: 'false' }});
        const data: ConfigRecord = eventRsp.data;
        setConfigRecord(data);
        
        const updatedValidationMessages: string[][] = [];
        data.secretaryEvents.forEach((e: SecretaryEventConfig, i: number) => {
          updatedValidationMessages.push([]);
        });
        setValidationMessages(updatedValidationMessages);
        data.secretaryEvents.forEach((e: SecretaryEventConfig, i: number) => {
          setValidationMessages(validateEventFields(data.secretaryEvents, i, clubNumbers, updatedValidationMessages));
        });
        
        setIsLoading(false);
      } catch(err: any) {
        console.log('get config/events err=' + JSON.stringify(err, null, 2));
        setIsLoading(false);
      }
    }
    if (jwtToken && email && !configRecord) {
      fetchData();
    }
  }, [clubNumbers, email, jwtToken, configRecord]);

  const getEventTypes = () => {
    return [ 'Agility', 'Obedience', 'Rally', 'Obedience and Rally' ];
  }

  const handleClickAddEvent = () => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    
    const newEvent: SecretaryEventConfig = {
      configId: '',
      clubName: '',
      location: '',
      dates: [],
      eventType: 'Rally',
      openingDateTime: '',
      closingDateTime: '',
      judges: ['TBD'],
      files: [],
      links: [],
      startAvailableDate: '',
      endAvailableDate: '',
      messages: [],
      urgentMessages: []
    }
    updatedConfigRecord.secretaryEvents.push(newEvent);
    setConfigRecord(updatedConfigRecord);
    const updatedValidationMessages = _.cloneDeep(validationMessages);
    updatedValidationMessages.push([]);
    setValidationMessages(validateEventFields(updatedConfigRecord.secretaryEvents, updatedConfigRecord.secretaryEvents.length - 1, clubNumbers, updatedValidationMessages));
  }

  const handleClickDeleteEvent = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents.splice(eventIndex, 1);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickSaveEvents = () => {
    /* istanbul ignore next */
    if (!configRecord) {
      setStatusMessage(`Save events failed at ${moment(Date.now()).format('MM/DD/YY hh:mm a')}`);
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    return axios.post(`/api/protected/config/events`, updatedConfigRecord, { params: { email }, headers: { Authorization: `Bearer ${jwtToken}`}})
    .then((rsp: any) => {
      setStatusMessage(`Save events successful at ${moment(Date.now()).format('MM/DD/YY hh:mm a')}`);
      setIsDirty(false);
    });
  }

  const handleClickMoveDownEvent = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    const event = _.cloneDeep(updatedConfigRecord.secretaryEvents[eventIndex]);
    updatedConfigRecord.secretaryEvents.splice(eventIndex, 1);
    updatedConfigRecord.secretaryEvents.splice(eventIndex + 1, 0, event);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickMoveUpEvent = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    const event = _.cloneDeep(updatedConfigRecord.secretaryEvents[eventIndex]);
    updatedConfigRecord.secretaryEvents.splice(eventIndex, 1);
    updatedConfigRecord.secretaryEvents.splice(eventIndex - 1, 0, event);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickAddDate = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    let newDateStr = '';
    if (updatedConfigRecord.secretaryEvents[eventIndex].dates.length > 0) {
      const datesLength = updatedConfigRecord.secretaryEvents[eventIndex].dates.length;
      const lastDate = updatedConfigRecord.secretaryEvents[eventIndex].dates[datesLength - 1];
      const lastDateISO = `${lastDate.split('/')[2]}-${lastDate.split('/')[0]}-${lastDate.split('/')[1]}T12:00:00.000Z`;
      const lastDateEpoch = new Date(lastDateISO).getTime();
      const newDateEpoch = new Date(lastDateEpoch + 1000 * 60 * 60 * 24).getTime();
      newDateStr = moment(newDateEpoch).format('MM/DD/YYYY');
    }
    updatedConfigRecord.secretaryEvents[eventIndex].dates.push(newDateStr);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickDeleteDate = (eventIndex: number, index: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].dates.splice(index, 1);
    setConfigRecord(updatedConfigRecord);
    setValidationMessages(validateEventFields(updatedConfigRecord.secretaryEvents, eventIndex, clubNumbers, validationMessages));
    setIsDirty(true);
  }

  const handleClickAddJudge = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].judges.push('');
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickDeleteJudge = (eventIndex: number, index: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].judges.splice(index, 1);
    setConfigRecord(updatedConfigRecord);
    setValidationMessages(validateEventFields(updatedConfigRecord.secretaryEvents, eventIndex, clubNumbers, validationMessages));
    setIsDirty(true);
  }

  const handleClickAddFile = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].files.push({ label: '', url: 'https://cloud-object-storage-24-cos-standard-id6.s3.us-east.cloud-object-storage.appdomain.cloud/' });
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickDeleteFile = (eventIndex: number, index: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].files.splice(index, 1);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickAddLink = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].links.push({ label: '', url: 'https://www.tfletservices.com/' });
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickDeleteLink = (eventIndex: number, index: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].links.splice(index, 1);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickAddMessage = (eventIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].messages.push('');
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleClickDeleteMessage = (eventIndex: number, index: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    updatedConfigRecord.secretaryEvents[eventIndex].messages.splice(index, 1);
    setConfigRecord(updatedConfigRecord);
    setIsDirty(true);
  }

  const handleChangeField = (newValue: string, eventIndex: number, fieldName: string, fieldIndex: number) => {
    /* istanbul ignore next */
    if (!configRecord) {
      return;
    }
    const updatedConfigRecord = _.cloneDeep(configRecord);
    const updatedEvent = updatedConfigRecord.secretaryEvents[eventIndex];

    if (fieldName === 'configId') {
      updatedEvent.configId = newValue;
      const clubNumberIndex = _.findIndex(clubNumbers, val => newValue.indexOf(val.abbreviation) > -1);
      if (clubNumberIndex > -1 && updatedEvent.clubName === '') {
        updatedEvent.clubName = clubNumbers[clubNumberIndex].name;
      }
    } 
    if (fieldName === 'clubName') {
      updatedEvent.clubName = newValue;
    }
    if (fieldName === 'eventType') {
      updatedEvent.eventType = newValue;
    }
    if (fieldName === 'openingDateTime') {
      updatedEvent.openingDateTime = (newValue as any).toISOString();
    }
    if (fieldName === 'closingDateTime') {
      updatedEvent.closingDateTime = (newValue as any).toISOString();
    }
    if (fieldName === 'startAvailableDate') {
      updatedEvent.startAvailableDate = (newValue as any).toISOString();
    }
    if (fieldName === 'endAvailableDate') {
      updatedEvent.endAvailableDate = (newValue as any).toISOString();
    }
    if (fieldName === 'date') {
      updatedEvent.dates[fieldIndex] = newValue;
      if (fieldIndex === 0 && newValue.match(/^\d\d\/\d\d\/\d\d\d\d$/)) {
        const eventDateISO = `${newValue.split('/')[2]}-${newValue.split('/')[0]}-${newValue.split('/')[1]}T12:00:00.000Z`;
        const eventDateEpoch = new Date(eventDateISO).getTime();
        updatedEvent.openingDateTime = new Date(eventDateEpoch - 1000 * 60 * 60 * 1416).toISOString()
        updatedEvent.closingDateTime = new Date(eventDateEpoch - 1000 * 60 * 60 * 398).toISOString();
        updatedEvent.startAvailableDate = moment(new Date(eventDateEpoch - 1000 * 60 * 60 * 24 * 80)).format('MM/DD/YYYY')
        updatedEvent.endAvailableDate = moment(new Date(eventDateEpoch + 1000 * 60 * 60 * 24 * 10)).format('MM/DD/YYYY')
      }
    }
    if (fieldName === 'judge') {
      updatedEvent.judges[fieldIndex] = newValue;
    }
    if (fieldName === 'files.label') {
      updatedEvent.files[fieldIndex].label = newValue;
    }
    if (fieldName === 'files.url') {
      updatedEvent.files[fieldIndex].url = newValue;
    }
    if (fieldName === 'links.label') {
      updatedEvent.links[fieldIndex].label = newValue;
    }
    if (fieldName === 'links.url') {
      updatedEvent.links[fieldIndex].url = newValue;
    }
    if (fieldName === 'messages') {
      updatedEvent.messages[fieldIndex] = newValue;
    }
    setConfigRecord(updatedConfigRecord);
    setValidationMessages(validateEventFields(updatedConfigRecord.secretaryEvents, eventIndex, clubNumbers, validationMessages));
    setIsDirty(true);
  }

  const validateEventFields = (events: SecretaryEventConfig[], eventIndex: number, akcClubNumbers: AkcClubNumber[], messages: string[][]) => {
    const updatedValidationMessages = _.cloneDeep(messages);
    updatedValidationMessages[eventIndex] = [];
    const newMsgs: string[] = [];
    const myEvent = events[eventIndex];

    if (_.findIndex(akcClubNumbers, (val: AkcClubNumber) => myEvent.configId.startsWith(val.abbreviation)) === -1) {
      newMsgs.push(`ConfigId prefix is not recognized. Update trialsec-constants akc-club-numbers if necessary.`)
    }
    if (myEvent.configId !== '' && myEvent.dates.length > 0) {
      const dateYY = myEvent.dates[0].substring(8);
      const dateMM = myEvent.dates[0].substring(0, 2);
      const configIdMMYY = myEvent.configId.split('-')[1];
      if (configIdMMYY !== `${dateMM}${dateYY}`) {
        newMsgs.push(`ConfigId suffix '${configIdMMYY}' is not consistent with the month '${dateMM}' and year '${dateYY}' in the date '${myEvent.dates[0]}' of the event.`)
      }
    }
    if (myEvent.configId !== '' && myEvent.clubName !== '') {
      const akcClubNumbersIndex = _.findIndex(akcClubNumbers, (val: AkcClubNumber) => myEvent.configId.startsWith(val.abbreviation));
      if (akcClubNumbersIndex > -1) {
        if (myEvent.clubName !== akcClubNumbers[akcClubNumbersIndex].name) {
          newMsgs.push(`When configId starts with '${myEvent.configId.split('-')[0]}' the expected club name is '${akcClubNumbers[akcClubNumbersIndex].name}'.`);
        }
      }
    }
    if (myEvent.dates.length === 0) {
      newMsgs.push('At least one date needs to be defined. Typically there will be 2 or 3 days of events.');
    }
    if (myEvent.judges.length === 0) {
      newMsgs.push(`At least one judge needs to be defined. Use 'TBD' if not assigned yet.`);
    }
    updatedValidationMessages[eventIndex] = newMsgs;
    return updatedValidationMessages;
  }
  return (
    <>
      { isLoading ? 
          <h1>Loading...</h1>
          :
          <>
            <div>
              <Button
                id={`config-view-secretary-events-add-event-button-top`}
                color="primary" 
                variant="contained"
                style={{textTransform: 'none'}}
                onClick={(e: any) => handleClickAddEvent()}>
                Add Event
              </Button> 
              <Button
                id={`config-view-secretary-events-save-events-button-top`}
                color="primary" 
                variant="contained"
                disabled={!isDirty}
                className='config-view-secretary-events-button'
                style={{textTransform: 'none', marginLeft: '20px' }}
                onClick={(e: any) => handleClickSaveEvents()}>
                Save Events
              </Button> 
            </div>
            { configRecord && configRecord.secretaryEvents.map((ev: SecretaryEventConfig, eventIndex: number) => (
              <Grid container direction="row" justifyContent="center" alignItems="center" key={eventIndex} className='config-view-secretary-events-card'>
                <Grid item key={`config-view-secretary-events-${eventIndex}-delete-button`} md={4} xs={12} p={1}>
                  <Button
                    id={`config-view-secretary-events-${eventIndex}-delete-button`}
                    color="primary" 
                    variant="contained"
                    style={{textTransform: 'none'}}
                    onClick={(e: any) => handleClickDeleteEvent(eventIndex)}>
                    Delete Event
                  </Button> 
                </Grid>
                <Grid item key={`config-view-secretary-events-${eventIndex}-move-up-button`} md={4} xs={12} p={1}>
                  <Button
                    id={`config-view-secretary-events-${eventIndex}-move-up-button`}
                    color="primary" 
                    variant="contained"
                    style={{textTransform: 'none'}}
                    onClick={(e: any) => handleClickMoveUpEvent(eventIndex)}>
                    Move Up Event
                  </Button> 
                </Grid>
                <Grid item key={`config-view-secretary-events-${eventIndex}-move-down-button`} md={4} xs={12} p={1}>
                  <Button
                    id={`config-view-secretary-events-${eventIndex}-move-down-button`}
                    color="primary" 
                    variant="contained"
                    style={{textTransform: 'none'}}
                    onClick={(e: any) => handleClickMoveDownEvent(eventIndex)}>
                    Move Down Event
                  </Button> 
                </Grid>
                <Grid item md={2} xs={12} p={1}>
                    <TextField 
                      id={`config-view-secretary-events-config-id-${eventIndex}`}
                      variant="outlined"
                      size="small"
                      className="config-view-secretary-events-field"
                      onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'configId', 0)}
                      value={ev.configId}
                      label="Config Id" />
                </Grid>
                <Grid item md={3} xs={12} p={1}>
                  <Select
                    id={`config-view-secretary-events-event-type-select-${eventIndex}`}
                    label="Event Type"
                    size='small'
                    style={{width: 300}}
                    value={ev.eventType}
                    onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'eventType', 0)}>
                    { getEventTypes().map((val: string, index: number) => (
                      <MenuItem key={`config-view-secretary-events-event-type-select-${eventIndex}-${index}`} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item md={5} xs={12} p={1}>
                  <TextField 
                      id={`config-view-secretary-events-club-name-${eventIndex}`}
                      variant="outlined"
                      size="small"
                      className="config-view-secretary-events-field"
                      onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'clubName', 0)}
                      value={ev.clubName}
                      label="Club Name" />
                </Grid>
                <Grid container>
                  { ev.dates.length === 0 ? 
                    <Grid item key={`config-view-secretary-events-dates-${eventIndex}-add-button`} md={2} xs={12} p={1}>
                      <Button
                        id={`config-view-secretary-events-dates-${eventIndex}-add-button`}
                        color="primary" 
                        variant="contained"
                        style={{textTransform: 'none'}}
                        onClick={(e: any) => handleClickAddDate(eventIndex)}>
                        Add Date
                      </Button> 
                    </Grid>
                    : null
                  }
                  { ev.dates.map((date: string, dateIndex: number) => (
                    <React.Fragment key={`config-view-secretary-events-dates-${eventIndex}-${dateIndex}`}>
                      <Grid item md={2} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-dates-${eventIndex}-${dateIndex}`}
                          variant="outlined"
                          size="small"
                          error={!date.match(/^\d\d\/\d\d\/\d\d\d\d$/)}
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'date', dateIndex)}
                          value={date}
                          label="Event Date (MM/DD/YYYY)" />
                        <Tooltip title="Delete date">
                          <IconButton 
                            id={`config-view-secretary-events-dates-${eventIndex}-${dateIndex}-delete-button`}
                            key={dateIndex}
                            color="primary" 
                            aria-label="delete date"
                            component="span"
                            onClick={() => handleClickDeleteDate(eventIndex, dateIndex)}>
                            <DeleteIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      { dateIndex === ev.dates.length - 1 ? 
                        <Grid item key={`config-view-secretary-events-dates-${eventIndex}-${dateIndex}-add-button`} md={2} xs={12} p={1}>
                          <Button
                            id={`config-view-secretary-events-dates-${eventIndex}-${dateIndex}-add-button`}
                            color="primary" 
                            variant="contained"
                            style={{textTransform: 'none'}}
                            onClick={(e: any) => handleClickAddDate(eventIndex)}>
                            Add Date
                          </Button> 
                        </Grid>
                        : null
                      }
                    </React.Fragment>
                  ))}
                </Grid>
                <Grid container>
                  { ev.judges.length === 0 ?
                    <>
                      <Grid item key={`config-view-secretary-events-judges-${eventIndex}-add-button`} md={2} xs={12} p={1}>
                        <Button
                          id={`config-view-secretary-events-judges-${eventIndex}-add-button`}
                          color="primary" 
                          variant="contained"
                          style={{textTransform: 'none'}}
                          onClick={(e: any) => handleClickAddJudge(eventIndex)}>
                          Add Judge
                        </Button> 
                      </Grid>
                    </>
                    : null
                  }
                  { ev.judges.map((judge: string, judgeIndex: number) => (
                    <React.Fragment key={`config-view-secretary-events-judges-${eventIndex}-${judgeIndex}`}>
                      <Grid item md={2} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-judges-${eventIndex}-${judgeIndex}`}
                          variant="outlined"
                          size="small"
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'judge', judgeIndex)}
                          value={judge}
                          label="Judge" />
                        <Tooltip title="Delete judge">
                          <IconButton 
                            id={`config-view-secretary-events-judges-${eventIndex}-${judgeIndex}-delete-button`}
                            key={judgeIndex}
                            color="primary" 
                            aria-label="delete judge"
                            component="span"
                            onClick={() => handleClickDeleteJudge(eventIndex, judgeIndex)}>
                            <DeleteIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      { judgeIndex === ev.judges.length - 1 ? 
                        <Grid item key={`config-view-secretary-events-judges-${eventIndex}-${judgeIndex}-add-button`} md={2} xs={12} p={1}>
                          <Button
                            id={`config-view-secretary-events-judges-${eventIndex}-${judgeIndex}-add-button`}
                            color="primary" 
                            variant="contained"
                            style={{textTransform: 'none'}}
                            onClick={(e: any) => handleClickAddJudge(eventIndex)}>
                            Add Judge
                          </Button> 
                        </Grid>
                        : null
                      }
                    </React.Fragment>
                  ))}
                </Grid>
                <Grid container>
                  { ev.files.length === 0 ?
                    <>
                      <Grid item key={`config-view-secretary-events-files-${eventIndex}-add-button`} md={2} xs={12} p={1}>
                        <Button
                          id={`config-view-secretary-events-files-${eventIndex}-add-button`}
                          color="primary" 
                          variant="contained"
                          style={{textTransform: 'none'}}
                          onClick={(e: any) => handleClickAddFile(eventIndex)}>
                          Add File
                        </Button> 
                      </Grid>
                    </>
                    : null
                  }
                  { ev.files.map((file: LabelAndUrl, fileIndex: number) => (
                    <React.Fragment key={`config-view-secretary-events-files-${eventIndex}-${fileIndex}`}>
                      <Grid item md={2} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-files-label-${eventIndex}-${fileIndex}`}
                          variant="outlined"
                          size="small"
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'files.label', fileIndex)}
                          value={file.label}
                          label="File Label" />
                        <Tooltip title="Delete file">
                          <IconButton 
                            id={`config-view-secretary-events-files-${eventIndex}-${fileIndex}-delete-button`}
                            key={fileIndex}
                            color="primary" 
                            aria-label="delete file"
                            component="span"
                            onClick={() => handleClickDeleteFile(eventIndex, fileIndex)}>
                            <DeleteIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      <Grid item md={9} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-files-url-${eventIndex}-${fileIndex}`}
                          variant="outlined"
                          size="small"
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'files.url', fileIndex)}
                          value={file.url}
                          label="File URL" />
                      </Grid>
                      { fileIndex === ev.files.length - 1 ? 
                        <Grid item key={`config-view-secretary-events-files-${eventIndex}-${fileIndex}-add-button`} md={1} xs={12} p={1}>
                          <Button
                            id={`config-view-secretary-events-files-${eventIndex}-${fileIndex}-add-button`}
                            color="primary" 
                            variant="contained"
                            style={{textTransform: 'none'}}
                            onClick={(e: any) => handleClickAddFile(eventIndex)}>
                            Add File
                          </Button> 
                        </Grid>
                        : null
                      }
                    </React.Fragment>
                  ))}
                </Grid>
                <Grid container>
                  { ev.links.length === 0 ?
                    <>
                      <Grid item key={`config-view-secretary-events-links-${eventIndex}-add-button`} md={2} xs={12} p={1}>
                        <Button
                          id={`config-view-secretary-events-links-${eventIndex}-add-button`}
                          color="primary" 
                          variant="contained"
                          style={{textTransform: 'none'}}
                          onClick={(e: any) => handleClickAddLink(eventIndex)}>
                          Add Link
                        </Button> 
                      </Grid>
                    </>
                    : null
                  }
                  { ev.links.map((link: LabelAndUrl, linkIndex: number) => (
                    <React.Fragment key={`config-view-secretary-events-links-${eventIndex}-${linkIndex}`}>
                      <Grid item md={2} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-links-label-${eventIndex}-${linkIndex}`}
                          variant="outlined"
                          size="small"
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'links.label', linkIndex)}
                          value={link.label}
                          label="Link Label" />
                        <Tooltip title="Delete link">
                          <IconButton 
                            id={`config-view-secretary-events-links-${eventIndex}-${linkIndex}-delete-button`}
                            key={linkIndex}
                            color="primary" 
                            aria-label="delete link"
                            component="span"
                            onClick={() => handleClickDeleteLink(eventIndex, linkIndex)}>
                            <DeleteIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      <Grid item md={9} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-links-url-${eventIndex}-${linkIndex}`}
                          variant="outlined"
                          size="small"
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'links.url', linkIndex)}
                          value={link.url}
                          label="Link URL" />
                      </Grid>
                      { linkIndex === ev.links.length - 1 ? 
                        <Grid item key={`config-view-secretary-events-links-${eventIndex}-${linkIndex}-add-button`} md={1} xs={12} p={1}>
                          <Button
                            id={`config-view-secretary-events-links-${eventIndex}-${linkIndex}-add-button`}
                            color="primary" 
                            variant="contained"
                            style={{textTransform: 'none'}}
                            onClick={(e: any) => handleClickAddLink(eventIndex)}>
                            Add Link
                          </Button> 
                        </Grid>
                        : null
                      }
                    </React.Fragment>
                  ))}
                </Grid>
                <Grid container>
                  { ev.messages.length === 0 ?
                    <>
                      <Grid item key={`config-view-my-messages-links-${eventIndex}-add-button`} md={2} xs={12} p={1}>
                        <Button
                          id={`config-view-secretary-events-messages-${eventIndex}-add-button`}
                          color="primary" 
                          variant="contained"
                          style={{textTransform: 'none'}}
                          onClick={(e: any) => handleClickAddMessage(eventIndex)}>
                          Add Message
                        </Button> 
                      </Grid>
                    </>
                    : null
                  }
                  { ev.messages.map((message: string, messageIndex: number) => (
                    <React.Fragment key={`config-view-secretary-events-messages-${eventIndex}-${messageIndex}`}>
                      <Grid item md={10} xs={12} p={1}>
                        <TextField 
                          id={`config-view-secretary-events-messages-${eventIndex}-${messageIndex}`}
                          variant="outlined"
                          size="small"
                          className="config-view-secretary-events-field"
                          onChange={(e: any) => handleChangeField(e.target.value, eventIndex, 'messages', messageIndex)}
                          value={message}
                          label="Message" />
                        <Tooltip title="Delete message">
                          <IconButton 
                            id={`config-view-secretary-events-messages-${eventIndex}-${messageIndex}-delete-button`}
                            key={messageIndex}
                            color="primary" 
                            aria-label="delete message"
                            component="span"
                            onClick={() => handleClickDeleteMessage(eventIndex, messageIndex)}>
                            <DeleteIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      { messageIndex === ev.messages.length - 1 ? 
                        <Grid item key={`config-view-secretary-events-messages-${eventIndex}-${messageIndex}-add-button`} md={2} xs={12} p={1}>
                          <Button
                            id={`config-view-secretary-events-messages-${eventIndex}-${messageIndex}-add-button`}
                            color="primary" 
                            variant="contained"
                            style={{textTransform: 'none'}}
                            onClick={(e: any) => handleClickAddMessage(eventIndex)}>
                            Add Message
                          </Button> 
                        </Grid>
                        : null
                      }
                    </React.Fragment>
                  ))}
                </Grid>
                <Grid item md={3} xs={12} p={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="Opening Date"
                      value={ev.openingDateTime}
                      onChange={(newValue: any) => handleChangeField(newValue, eventIndex, 'openingDateTime', 0)}
                      renderInput={(params: any) => <TextField { ...params } id={`config-view-secretary-events-${eventIndex}-opening-date`} sx={{ m: 1 }} />}
                    />
                  </LocalizationProvider> 
                </Grid>
                <Grid item md={3} xs={12} p={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="Closing Date"
                      value={ev.closingDateTime}
                      onChange={(newValue: any) => handleChangeField(newValue, eventIndex, 'closingDateTime', 0)}
                      renderInput={(params: any) => <TextField { ...params } id={`config-view-secretary-events-${eventIndex}-closing-date`} sx={{ m: 1 }} />}
                    />
                  </LocalizationProvider> 
                </Grid>
                <Grid item md={3} xs={12} p={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Available Date"
                      value={ev.startAvailableDate}
                      onChange={(newValue: any) => handleChangeField(newValue, eventIndex, 'startAvailableDate', 0)}
                      renderInput={(params: any) => <TextField { ...params } id={`config-view-secretary-events-${eventIndex}-start-available-date`} sx={{ m: 1 }} />}
                    />
                  </LocalizationProvider> 
                </Grid>
                <Grid item md={3} xs={12} p={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Available Date"
                      value={ev.endAvailableDate}
                      onChange={(newValue: any) => handleChangeField(newValue, eventIndex, 'endAvailableDate', 0)}
                      renderInput={(params: any) => <TextField { ...params } id={`config-view-secretary-events-${eventIndex}-end-available-date`} sx={{ m: 1 }} />}
                    />
                  </LocalizationProvider> 
                </Grid>
                { validationMessages[eventIndex].length > 0 ?
                  <Grid item key={`config-view-secretary-events-validation-header-${eventIndex}`} md={12} xs={12} p={1}>
                    <div className='config-view-secretary-events-validation-errors-header'>Validation Errors</div>
                  </Grid>
                  : null
                }
                { validationMessages[eventIndex].map((val: string, msgArrIndex: number) => (
                  <Grid item key={`config-view-secretary-events-validation-errors-${eventIndex}-${msgArrIndex}`} md={12} xs={12} p={1}>
                    <div className='config-view-secretary-events-validation-errors-line' id={`config-view-secretary-events-validation-errors-${eventIndex}-${msgArrIndex}`}>{ val }</div>
                  </Grid>
                ))}
              </Grid>
            ))}
            <div className='config-view-secretary-events-bottom-buttons-wrapper'>
              <Button
                id={`config-view-secretary-events-add-event-button-bottom`}
                color="primary" 
                variant="contained"
                style={{textTransform: 'none'}}
                onClick={(e: any) => handleClickAddEvent()}>
                Add Event
              </Button> 
              <Button
                id={`config-view-secretary-events-save-events-button-bottom`}
                color="primary" 
                variant="contained"
                disabled={!isDirty}
                className='config-view-secretary-events-button'
                style={{textTransform: 'none', marginLeft: '20px' }}
                onClick={(e: any) => handleClickSaveEvents()}>
                Save Events
              </Button> 
            </div>
            <div className="config-view-secretary-events-status-message">
              { statusMessage }
            </div>
          </>
      }
    </>
  )
}